import { useCallback, useRef } from 'react';

// Sound frequencies and durations for different actions
const SOUNDS = {
  addToCart: {
    frequencies: [523.25, 659.25, 783.99],
    duration: 0.15,
    type: 'sine' as OscillatorType,
  },
  click: {
    frequencies: [800],
    duration: 0.05,
    type: 'sine' as OscillatorType,
  },
  notification: {
    frequencies: [587.33, 783.99],
    duration: 0.12,
    type: 'sine' as OscillatorType,
  },
  messageSent: {
    frequencies: [698.46, 880],  // F5, A5 — ascending whoosh
    duration: 0.1,
    type: 'sine' as OscillatorType,
  },
  messageReceived: {
    frequencies: [440, 523.25],  // A4, C5 — soft pop
    duration: 0.08,
    type: 'triangle' as OscillatorType,
  },
};

export const useSounds = () => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const isEnabledRef = useRef(true);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  const playSound = useCallback((soundType: keyof typeof SOUNDS) => {
    if (!isEnabledRef.current) return;

    try {
      const audioContext = getAudioContext();
      const sound = SOUNDS[soundType];
      const now = audioContext.currentTime;

      sound.frequencies.forEach((freq, index) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.type = sound.type;
        oscillator.frequency.setValueAtTime(freq, now);

        // Envelope for smooth sound
        gainNode.gain.setValueAtTime(0, now);
        gainNode.gain.linearRampToValueAtTime(0.15, now + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.001, now + sound.duration);

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        const startTime = now + (index * 0.03); // Slight delay for chord effect
        oscillator.start(startTime);
        oscillator.stop(startTime + sound.duration);
      });
    } catch (error) {
      console.warn('Sound playback failed:', error);
    }
  }, [getAudioContext]);

  const playAddToCart = useCallback(() => playSound('addToCart'), [playSound]);
  const playClick = useCallback(() => playSound('click'), [playSound]);
  const playNotification = useCallback(() => playSound('notification'), [playSound]);
  const playMessageSent = useCallback(() => playSound('messageSent'), [playSound]);
  const playMessageReceived = useCallback(() => playSound('messageReceived'), [playSound]);

  const toggleSounds = useCallback((enabled: boolean) => {
    isEnabledRef.current = enabled;
  }, []);

  return {
    playAddToCart,
    playClick,
    playNotification,
    playMessageSent,
    playMessageReceived,
    toggleSounds,
  };
};

// Singleton instance for global access
let globalSoundInstance: ReturnType<typeof useSounds> | null = null;

export const getGlobalSounds = () => {
  if (!globalSoundInstance) {
    // Create a simple implementation for non-React contexts
    let audioContext: AudioContext | null = null;
    let isEnabled = true;

    const getContext = () => {
      if (!audioContext) {
        audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      return audioContext;
    };

    const playSound = (soundType: keyof typeof SOUNDS) => {
      if (!isEnabled) return;

      try {
        const ctx = getContext();
        const sound = SOUNDS[soundType];
        const now = ctx.currentTime;

        sound.frequencies.forEach((freq, index) => {
          const oscillator = ctx.createOscillator();
          const gainNode = ctx.createGain();

          oscillator.type = sound.type;
          oscillator.frequency.setValueAtTime(freq, now);

          gainNode.gain.setValueAtTime(0, now);
          gainNode.gain.linearRampToValueAtTime(0.15, now + 0.01);
          gainNode.gain.exponentialRampToValueAtTime(0.001, now + sound.duration);

          oscillator.connect(gainNode);
          gainNode.connect(ctx.destination);

          const startTime = now + (index * 0.03);
          oscillator.start(startTime);
          oscillator.stop(startTime + sound.duration);
        });
      } catch (error) {
        console.warn('Sound playback failed:', error);
      }
    };

    globalSoundInstance = {
      playAddToCart: () => playSound('addToCart'),
      playClick: () => playSound('click'),
      playNotification: () => playSound('notification'),
      playMessageSent: () => playSound('messageSent'),
      playMessageReceived: () => playSound('messageReceived'),
      toggleSounds: (enabled: boolean) => { isEnabled = enabled; },
    };
  }
  return globalSoundInstance;
};
